import type {InitialConfig} from '../model';
import {
  ConfigGroups,
  ConfigKey,
  ConfigRegexValidation,
  DataType,
  EntitlementsModules,
} from '../model';

export const EMPLOYEE_AUTO_LOGOUT_TIME: InitialConfig = {
  key: ConfigKey.EMPLOYEE_AUTO_LOGOUT_TIME,
  label: {
    pl: 'Czas automatycznego wylogowania pracownika',
    en: 'Auto-logout employee time',
    de: 'Automatische Abmeldezeit des Mitarbeiters',
    uk: 'Час автоматичного виходу працівника',
  },
  description: {
    pl: 'Czas podawany w sekundach, maksymalnie 999 sekund',
    en: 'Time given in seconds, maximum 999 seconds',
    de: 'Zeit in Sekunden angegeben, maximal 999 Sekunden',
    uk: 'Час, вказаний у секундах, mаксимум 999 секунд',
  },
  value: {
    numberValue: 300,
  },

  group: ConfigGroups.GENERAL,
  dataType: DataType.NUMBER,
  regex: ConfigRegexValidation.POSITIVE_NUMBERS,
};

export const ADMIN_AUTO_LOGOUT_TIME: InitialConfig = {
  key: ConfigKey.ADMIN_AUTO_LOGOUT_TIME,
  label: {
    pl: 'Czas automatycznego wylogowania admina',
    en: 'Auto-logout admin time',
    de: 'Automatische Abmeldezeit des Administrators',
    uk: 'Час автоматичного виходу адміністратора',
  },
  description: {
    pl: 'Czas podawany w sekundach, maksymalnie 999 sekund',
    en: 'Time given in seconds, maximum 999 seconds',
    de: 'Zeit in Sekunden angegeben, maximal 999 Sekunden',
    uk: 'Час, вказаний у секундах, mаксимум 999 секунд',
  },
  value: {
    numberValue: 300,
  },
  group: ConfigGroups.GENERAL,
  dataType: DataType.NUMBER,
  regex: ConfigRegexValidation.POSITIVE_NUMBERS,
};

export const SHIFT_HOUR_START: InitialConfig = {
  key: ConfigKey.SHIFT_HOUR_START,
  label: {
    pl: 'Godzina o której zaczyna się doba pracownicza',
    en: 'Hour on which today shift starts',
    de: 'Uhrzeit, zu der der Arbeitstag beginnt',
    uk: 'Час, о котрій починається робочий день',
  },
  description: {
    pl: 'Godzina od 0 do 23',
    en: 'Hour from 0 to 23',
    de: 'Stunden von 0 bis 23',
    uk: 'Години з 0 до 23',
  },
  value: {
    numberValue: 7,
  },
  group: ConfigGroups.GENERAL,
  dataType: DataType.NUMBER,
  regex: ConfigRegexValidation.NUMBERS_0_TO_23,
};

export const EMPLOYEE_SHIFT_DURATION_HOURS: InitialConfig = {
  key: ConfigKey.EMPLOYEE_SHIFT_DURATION_HOURS,
  label: {
    pl: 'Domyślny czas trwania zmiany pracownika',
    en: 'Default employee shift duration',
    de: 'Standarddauer der Mitarbeiterschicht',
    uk: 'Типова тривалість зміни працівника',
  },
  description: {
    pl: 'Liczba godzin, po której aktywna zmiana pracownika zostanie automatycznie zakończona. Wartość od 1 do 24.',
    en: 'Number of hours after which an active employee shift will be ended automatically. Value from 1 to 24.',
    de: 'Anzahl der Stunden, nach denen eine aktive Mitarbeiterschicht automatisch beendet wird. Wert von 1 bis 24.',
    uk: 'Кількість годин, після яких активну зміну працівника буде автоматично завершено. Значення від 1 до 24.',
  },
  value: {
    numberValue: 12,
  },
  group: ConfigGroups.GENERAL,
  dataType: DataType.NUMBER,
  regex: ConfigRegexValidation.NUMBERS_1_TO_24,
  requiredEntitlement: EntitlementsModules.WORK_TIME,
};

export const FORCE_EMPLOYEE_WORK_TIME_TRACKING: InitialConfig = {
  key: ConfigKey.FORCE_EMPLOYEE_WORK_TIME_TRACKING,
  label: {
    pl: 'Wymagaj od pracowników rejestrowania czasu pracy',
    en: 'Require employees to track work time',
    de: 'Arbeitszeiterfassung für Mitarbeitende vorschreiben',
    uk: 'Вимагати від працівників обліку робочого часу',
  },
  description: {
    pl: 'Pracownicy muszą rozpocząć pracę przed wejściem do aplikacji i są wylogowywani po jej zakończeniu. Ostrzeżenia o rejestrowaniu czasu są też wyświetlane grupom z opcjonalnym rejestrowaniem.',
    en: 'Employees must start work before entering the app and are signed out after finishing. Also shows tracking warnings to optional groups.',
    de: 'Mitarbeitende müssen die Arbeit vor dem Öffnen der App beginnen und werden nach Arbeitsende abgemeldet. Außerdem werden Gruppen mit optionaler Erfassung Warnungen angezeigt.',
    uk: 'Працівники повинні розпочати роботу перед входом у застосунок і виходять із системи після її завершення. Також відображаються попередження для груп із необов’язковим обліком.',
  },
  value: {
    boolValue: false,
  },
  group: ConfigGroups.GENERAL,
  dataType: DataType.BOOLEAN,
  regex: ConfigRegexValidation.BOOLEAN,
  requiredEntitlement: EntitlementsModules.WORK_TIME,
};

export const ALLOW_FILES_UPLOAD_FOR_CONTACT_PERSON: InitialConfig = {
  key: ConfigKey.ALLOW_FILES_UPLOAD_FOR_CONTACT_PERSON,
  label: {
    pl: 'Zezwól osobom kontaktowym na przesyłanie plików',
    en: 'Allow contact persons to upload files',
    de: 'Kontaktpersonen das Hochladen von Dateien erlauben',
    uk: 'Дозволити контактним особам завантажувати файли',
  },
  description: {
    pl: 'Pozwala na załączanie plików do aplikacji przez konto osoby kontaktowej',
    en: "Allows attaching files to the application through the contact person's account",
    de: 'Ermöglicht das Anhängen von Dateien an den Antrag über das Konto der Kontaktperson',
    uk: 'Дозволяє додавати файли до заявки через обліковий запис контактної особи',
  },
  value: {
    boolValue: false,
  },
  group: ConfigGroups.GENERAL,
  dataType: DataType.BOOLEAN,
  regex: ConfigRegexValidation.BOOLEAN,
};

export const CARE_FACILITY_PHONE_NUMBER: InitialConfig = {
  key: ConfigKey.CARE_FACILITY_PHONE_NUMBER,
  label: {
    pl: 'Numer telefonu placówki',
    en: 'Care facility phone number',
    de: 'Telefonnummer der Einrichtung',
    uk: 'Номер телефону закладу',
  },
  description: {
    pl: 'Numer telefonu w formacie +48123456789',
    en: 'Phone number in the format +48123456789',
    de: 'Telefonnummer im Format +48123456789',
    uk: 'Номер телефону у форматі +48123456789',
  },
  value: {
    stringValue: '',
  },
  group: ConfigGroups.CARE_FACILITY_DETAILS,
  dataType: DataType.STRING,
  regex: ConfigRegexValidation.PHONE_NUMBER_OR_EMPTY,
};

export const CARE_FACILITY_EMAIL: InitialConfig = {
  key: ConfigKey.CARE_FACILITY_EMAIL,
  label: {
    pl: 'Adres email placówki',
    en: 'Care facility email address',
    de: 'E-Mail-Adresse der Einrichtung',
    uk: 'Електронна адреса закладу',
  },
  description: {
    pl: 'Adres email w formacie nazwa@domena.pl',
    en: 'Email address in the format name@domain.com',
    de: 'E-Mail-Adresse im Format name@domain.de',
    uk: 'Електронна адреса у форматі name@domain.com',
  },
  value: {
    stringValue: '',
  },
  group: ConfigGroups.CARE_FACILITY_DETAILS,
  dataType: DataType.STRING,
  regex: ConfigRegexValidation.EMAIL,
};

export const CARE_FACILITY_ADDRESS: InitialConfig = {
  key: ConfigKey.CARE_FACILITY_ADDRESS,
  label: {
    pl: 'Adres placówki',
    en: 'Care facility address',
    de: 'Adresse der Einrichtung',
    uk: 'Адреса закладу',
  },
  description: {
    pl: 'Pełny adres pocztowy, np. ul. Przykładowa 1, 00-000 Warszawa',
    en: 'Full postal address, e.g. 1 Example St, 00-000 Warsaw',
    de: 'Vollständige Postanschrift, z.B. Musterstraße 1, 00-000 Musterstadt',
    uk: 'Повна поштова адреса, наприклад вул. Прикладна 1, 00-000 Варшава',
  },
  value: {
    stringValue: '',
  },
  group: ConfigGroups.CARE_FACILITY_DETAILS,
  dataType: DataType.STRING,
  regex: ConfigRegexValidation.ANY_OR_EMPTY,
};

export const CARE_FACILITY_NAME: InitialConfig = {
  key: ConfigKey.CARE_FACILITY_NAME,
  label: {
    pl: 'Nazwa placówki',
    en: 'Care facility name',
    de: 'Name der Einrichtung',
    uk: 'Назва закладу',
  },
  description: {
    pl: 'Pełna oficjalna nazwa placówki, np. Dom Opieki Słoneczna',
    en: 'Full official name of the facility, e.g. Sunny Care Home',
    de: 'Vollständiger offizieller Name der Einrichtung, z.B. Pflegeheim Sonnenschein',
    uk: 'Повна офіційна назва закладу, наприклад Будинок догляду Сонячний',
  },
  group: ConfigGroups.CARE_FACILITY_DETAILS,
  value: {
    stringValue: '',
  },
  dataType: DataType.STRING,
  regex: ConfigRegexValidation.ANY_OR_EMPTY,
};

export const CARE_FACILITY_LOGO: InitialConfig = {
  key: ConfigKey.CARE_FACILITY_LOGO,
  label: {
    pl: 'Logo placówki',
    en: 'Care facility logo',
    de: 'Logo der Einrichtung',
    uk: 'Логотип закладу',
  },
  description: {
    pl: 'Plik graficzny z logo placówki. < 1MB',
    en: 'Graphic file with the care facility logo. < 1MB',
    de: 'Grafikdatei mit dem Logo der Einrichtung. < 1MB',
    uk: 'Графічний файл з логотипом закладу. < 1MB',
  },
  value: {
    stringValue: '',
  },
  group: ConfigGroups.CARE_FACILITY_DETAILS,
  dataType: DataType.PUBLIC_IMAGE,
  regex: ConfigRegexValidation.IMAGE_FILE_NAME,
};

export const ANNOUNCE_RESIDENT_BIRTHDAY: InitialConfig = {
  key: ConfigKey.ANNOUNCE_RESIDENT_BIRTHDAY,
  label: {
    pl: 'Ogłaszaj urodziny rezydentów',
    en: 'Announce resident birthdays',
    de: 'Geburtstage der Bewohner ankündigen',
    uk: 'Оголошувати дні народження резидентів',
  },
  description: {
    pl: 'Włącz lub wyłącz ogłoszenia dotyczące urodzin rezydentów',
    en: 'Enable or disable announcements for resident birthdays',
    de: 'Aktivieren oder deaktivieren Sie Ankündigungen für Geburtstage der Bewohner',
    uk: 'Увімкнути або вимкнути оголошення про дні народження резидентів',
  },
  group: ConfigGroups.GENERAL,
  value: {
    boolValue: false,
  },
  dataType: DataType.BOOLEAN,
  regex: ConfigRegexValidation.BOOLEAN,
};

export const SHOW_EMPLOYEE_ABSENCES_ON_SCHEDULE: InitialConfig = {
  key: ConfigKey.SHOW_EMPLOYEE_ABSENCES_ON_SCHEDULE,
  label: {
    pl: 'Pokaż nieobecności pracowników w grafiku',
    en: 'Show employee absences on schedule',
    de: 'Abwesenheiten der Mitarbeiter im Dienstplan anzeigen',
    uk: 'Показувати відсутності працівників у розкладі',
  },
  description: {
    pl: 'Gdy włączone, pracownicy widzą w grafiku nieobecności innych pracowników (bez szczegółów). Gdy wyłączone, każdy pracownik widzi tylko swoje własne nieobecności.',
    en: "When enabled, employees can see other employees' absences on the schedule (without details). When disabled, each employee only sees their own absences.",
    de: 'Wenn aktiviert, können Mitarbeitende die Abwesenheiten anderer Mitarbeitender im Dienstplan sehen (ohne Details). Wenn deaktiviert, sieht jeder Mitarbeitende nur seine eigenen Abwesenheiten.',
    uk: 'Якщо увімкнено, працівники бачать відсутності інших працівників у розкладі (без деталей). Якщо вимкнено, кожен працівник бачить лише свої власні відсутності.',
  },
  group: ConfigGroups.GENERAL,
  value: {
    boolValue: false,
  },
  dataType: DataType.BOOLEAN,
  regex: ConfigRegexValidation.BOOLEAN,
};

export const FORCE_IP_CHECK_FOR_PAIRED_DEVICE: InitialConfig = {
  key: ConfigKey.FORCE_IP_CHECK_FOR_PAIRED_DEVICE,
  label: {
    pl: 'Wymuszaj sprawdzanie adresu IP dla sparowanych urządzeń',
    en: 'Force IP check for paired devices',
    de: 'IP-Prüfung für gekoppelte Geräte erzwingen',
    uk: 'Примусова перевірка IP-адреси для спарених пристроїв',
  },
  description: {
    pl: 'Gdy włączone, logowanie z nowego adresu IP wymaga osobnego zatwierdzenia przez administratora. Gdy wyłączone, zatwierdzone urządzenie może logować się z dowolnej sieci.',
    en: 'When enabled, logging in from a new IP address requires separate admin approval. When disabled, an approved device can log in from any network.',
    de: 'Wenn aktiviert, erfordert die Anmeldung von einer neuen IP-Adresse eine separate Genehmigung durch den Administrator. Wenn deaktiviert, kann sich ein genehmigtes Gerät aus jedem Netzwerk anmelden.',
    uk: 'Якщо увімкнено, вхід з нової IP-адреси потребує окремого підтвердження адміністратора. Якщо вимкнено, схвалений пристрій може входити з будь-якої мережі.',
  },
  group: ConfigGroups.GENERAL,
  value: {
    boolValue: false,
  },
  dataType: DataType.BOOLEAN,
  regex: ConfigRegexValidation.BOOLEAN,
};

export const DEFAULT_CONFIGURATIONS = [
  SHIFT_HOUR_START,
  EMPLOYEE_SHIFT_DURATION_HOURS,
  FORCE_EMPLOYEE_WORK_TIME_TRACKING,
  ADMIN_AUTO_LOGOUT_TIME,
  EMPLOYEE_AUTO_LOGOUT_TIME,
  ALLOW_FILES_UPLOAD_FOR_CONTACT_PERSON,
  ANNOUNCE_RESIDENT_BIRTHDAY,
  CARE_FACILITY_PHONE_NUMBER,
  CARE_FACILITY_NAME,
  CARE_FACILITY_EMAIL,
  CARE_FACILITY_LOGO,
  CARE_FACILITY_ADDRESS,
  FORCE_IP_CHECK_FOR_PAIRED_DEVICE,
  SHOW_EMPLOYEE_ABSENCES_ON_SCHEDULE,
];
